import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useHistory, useParams } from 'react-router-dom'
import { Alert } from '@material-ui/lab'
import { Box, Button, Grid, Snackbar } from '@material-ui/core'
import { FormProvider, useForm } from 'react-hook-form'
import isEmpty from 'lodash/isEmpty'
import clsx from 'clsx'
import { useBoolean } from '../../../../hooks'
import { ADMIN_ROUTES, FEATURE_FLAG, TEXT_VARIANTS } from '../../../../constants'
import ErrorComponent from '../../../atoms/ErrorComponent'
import Divider from '../../../atoms/Divider'
import Text from '../../../atoms/Text'
import Dialog from '../../../molecules/Dialog/Dialog'
import RoundedButton from '../../../atoms/RoundedButton'
import { initialAlertState } from '../common'
import SaveCancelButtons from '../../../molecules/SaveCancelButtons'
import { useFeatureFlag } from '../../../../redux/slices/appConfig'
import { useSetAppContext } from '../../../../redux/slices/appContext'
import { useClient, useClientBillingInfo, useInvalidateClientsQueries, useRemoveClient } from '../../../../api/clients'
import PrintViewModestyCurtain from '../../../molecules/PrintViewModestyCurtain'
import { useGroupSearch } from '../../../../api/groups'
import { getGroupsQuery, mapGroupsOptions } from '../LevelTags/helpers'
import LoadingView from '../../LoadingView'
import ClientForm from './ClientForm'
import ClientAccountsAssignment from './ClientAccountsAssignment'
import ClientBillingDetails from './ClientBillingDetails'
import ClientPersons from './ClientPersons'
import useClientAccounts from './useClientAccounts'
import {
  CLIENT_BILLING_FORM_NAMES,
  CLIENTS_MEMBER_LEVEL_ID,
  mapClient,
  mapFormDefaultValues,
  saveClientData
} from './helpers'
import ClientProfilePicture from './ClientProfilePicture'
import ClientDetailsTags from './ClientDetailsTags'
import { useAddGroups, useRemoveGroups } from './mutations'

const useStyles = makeStyles((theme) => ({
  container: ({ editMode }) => ({
    padding: `0rem 3rem ${editMode ? '5.5rem' : '1rem'} 3rem`
  }),
  formActions: {
    position: 'fixed',
    bottom: '1rem',
    left: '50%',
    padding: '0',
    transform: 'translateX(-50%)',
    width: 'fit-content',
    zIndex: 2
  },
  columnTitle: {
    textAlign: 'left',
    fontSize: '1.5rem'
  },
  column: {
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    padding: `${theme.spacing(4)}px 0`,
    display: 'flex'
  },
  divider: {
    margin: `${theme.spacing(4)}px 0`
  },
  tag: {
    marginRight: theme.spacing(1)
  },
  mainTitle: {
    fontSize: '2.125rem'
  },
  title: {
    textAlign: 'left',
    marginBottom: theme.spacing(2)
  },
  titleContainer: {
    width: '100%',
    marginLeft: '2rem',
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'center',
    flexDirection: 'column'
  },
  roundedButton: {
    border: `2px solid ${theme.palette.mirage}`,
    color: theme.palette.mirage,
    borderRadius: 25,
    padding: '4px 16px 3px 16px',
    textTransform: 'none',
    fontWeight: 'bold',
    fontSize: '0.75rem',
    alignSelf: 'flex-start',
    marginLeft: 'auto'
  },
  spaceDivider: {
    minHeight: '1rem',
    width: '100%'
  },
  button: {
    borderRadius: '30px',
    fontWeight: 800,
    textTransform: 'capitalize',
    padding: '14px 16px',
    width: '280px',
    marginRight: '10px',
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none'
    }
  },
  cancelBtn: {
    backgroundColor: theme.palette.lightCoral,
    color: theme.palette.error.main,
    '&:hover': {
      backgroundColor: '#f3b3be'
    }
  },
  confirmBtn: {
    backgroundColor: theme.palette.gray.light,
    color: theme.palette.lightBlack,
    '&:hover': {
      backgroundColor: theme.palette.gray.dark
    }
  }
}))

function ClientDetailsView () {
  const params = useParams()
  const history = useHistory()
  const setAppContext = useSetAppContext()

  const isNew = params.id === 'new'

  const [editMode, setIsEditMode] = useBoolean(isNew)
  const [formSubmitting, setIsFormSubmitting] = useBoolean()
  const [deleteDialog, setDeleteDialog] = useBoolean()

  const { active: isBillingActive } = useFeatureFlag(FEATURE_FLAG.SHOW_BILLING_INFO)
  const [alertState, setAlertState] = useState(initialAlertState)
  const { alertMessage, alertSeverity, openAlert } = alertState
  const [isDeletingUser, setIsDeletingUser] = useState(false)

  const classes = useStyles({ editMode })

  useEffect(() => {
    setAppContext({
      breadCrumbAliases: {
        [params.id]: isNew ? 'Client Details' : 'New Client'
      }
    })
  }, [setAppContext, isNew, params.id])

  const handleCloseAlert = useCallback((_, reason) => {
    if (reason === 'clickaway') return
    setAlertState(initialAlertState)
  }, [])

  const queryOptions = useMemo(() => ({ enabled: !isNew }), [isNew])

  const clientData = useClient(params.id, queryOptions)
  const clientBillingInfoData = useClientBillingInfo(params.id, queryOptions)

  const tagsQuery = useMemo(() => getGroupsQuery({
    memberLevelId: params.id,
    memberLevelTypeId: CLIENTS_MEMBER_LEVEL_ID
  }), [params.id])

  const tagsQueryOptions = useMemo(() => ({
    mapper: mapGroupsOptions,
    enabled: !isNew
  }), [isNew])

  const clientTagsData = useGroupSearch(tagsQuery, tagsQueryOptions)

  const client = useMemo(
    () => mapClient(
      clientData.data,
      clientBillingInfoData.data,
      clientTagsData.data
    ),
    [
      clientData.data,
      clientTagsData.data,
      clientBillingInfoData.data
    ]
  )

  const defaultValues = useMemo(() => {
    return mapFormDefaultValues(client)
  }, [client])

  const invalidateQueries = useInvalidateClientsQueries()

  const onSuccessRemoveClient = () => {
    invalidateQueries()
    history.push(ADMIN_ROUTES.CLIENTS)
  }

  const onSettledRemove = () => {
    setIsDeletingUser(false)
  }

  const {
    mutateAsync: removeClient,
    isLoading: isRemovingClient
  } = useRemoveClient(params.id, onSuccessRemoveClient, onSettledRemove)

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues
  })

  const {
    accountsAssigned,
    accountsUnAssigned,
    selectedAccounts,
    onSearchAccounts,
    searchResultOptions,
    onAssignUnAssignAccount,
    persistAccountsSelection,
    refetchAccountsAssigned
  } = useClientAccounts({
    clientId: !isNew ? params.id : null,
    resetState: editMode
  })

  const {
    reset,
    register,
    handleSubmit,
    formState: {
      isDirty: isFormDirty,
      isValid: _isFormValid,
      defaultValues: clientDefaultValues
    }
  } = formMethods

  const onResetForm = useCallback(() => {
    reset(mapFormDefaultValues(client))
  }, [client, reset])

  const onCancel = useCallback(() => {
    if (isNew) {
      return history.push(ADMIN_ROUTES.CLIENTS)
    }
    onResetForm()
    setIsEditMode.off()
  }, [history, onResetForm, setIsEditMode, isNew])

  const onRemoveClient = async () => {
    setIsDeletingUser(true)
    const updatedClient = await saveClientData({
      isNew,
      client: {
        ...client,
        feeScheduleType: '',
        feeOverride: []
      },
      clientDefaultValues,
      selectedAccounts
    })

    if (updatedClient) {
      removeClient()
    }
  }

  useEffect(() => {
    onResetForm()
  }, [onResetForm])

  const hasAccountsSelected =
    !isEmpty(selectedAccounts.assigned) ||
    !isEmpty(selectedAccounts.unAssigned)

  const isFormValid = _isFormValid && (isFormDirty || hasAccountsSelected)

  const { mutate: removeGroups } = useRemoveGroups()
  const { mutate: addGroups } = useAddGroups()

  const refetchClientData = useCallback(
    async ({ clientTags }) => {
      const { addTagsToGroup, removeTagsFromGroup } = clientTags

      if (!isEmpty(removeTagsFromGroup)) {
        const groupIds = removeTagsFromGroup.map(({ groupId }) => groupId)
        removeGroups(groupIds)
      }

      if (!isEmpty(addTagsToGroup)) {
        const groups = addTagsToGroup.map(({ groupId, groupTypeId }) => ({
          groupId,
          groupType: { groupTypeId }
        }))
        addGroups(groups)
      }

      await Promise.all([
        clientData.refetch(),
        clientBillingInfoData.refetch()
      ])
      refetchAccountsAssigned()
      persistAccountsSelection()
    },
    [
      clientData,
      addGroups,
      removeGroups,
      clientBillingInfoData,
      refetchAccountsAssigned,
      persistAccountsSelection
    ]
  )

  const onSubmit = useCallback(
    async (values) => {
      try {
        setIsFormSubmitting.on()
        const [client, clientBody] = await saveClientData({
          isNew,
          client: values,
          clientDefaultValues,
          selectedAccounts
        })
        if (isNew) {
          history.push(`${ADMIN_ROUTES.CLIENTS}/${client.clientId}`)
        } else {
          setTimeout(async () => {
            await refetchClientData(clientBody)
          }, 1000)
        }
      } catch (err) {
        console.error(err)
      } finally {
        setIsEditMode.off()
        setIsFormSubmitting.off()
      }
    },
    [
      isNew,
      history,
      setIsEditMode,
      selectedAccounts,
      refetchClientData,
      setIsFormSubmitting,
      clientDefaultValues
    ]
  )

  const handleCloseDialog = useCallback(() => {
    setDeleteDialog.off()
  }, [setDeleteDialog])

  const isLoading =
    (clientData.isLoading || clientBillingInfoData.isLoading) &&
    queryOptions.enabled

  if (clientData.error) {
    return <ErrorComponent error={clientData.error} />
  }

  if (isLoading) {
    return <LoadingView width='100%' height='100%' />
  }

  return (
    <>
      <PrintViewModestyCurtain text='Deleting Client' show={isDeletingUser} />
      <div className={classes.container}>
        {(!clientData.isLoading || isNew) && (
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              {client?.clientId && (
                <input
                  type='hidden'
                  {...register('clientId', { required: true })}
                />
              )}
              <div className={classes.header}>
                <ClientProfilePicture
                  clientId={client?.clientId}
                  disabled={!editMode}
                  disabledOpacity={1}
                  formMediaName={CLIENT_BILLING_FORM_NAMES.profilePicMediaId}
                  defaultMediaId={
                    client?.extras?.profileData?.profilePicMediaId
                  }
                  uploadLabel={editMode ? 'Upload' : ''}
                />
                <div className={classes.titleContainer}>
                  {editMode || isNew ? (
                    <ClientForm />
                  ) : (
                    <div>
                      <Text
                        text={client.longName}
                        className={classes.mainTitle}
                        variant={TEXT_VARIANTS.h1}
                      />
                      <Box my={2}>
                        <Text
                          text={client.shortName}
                          variant={TEXT_VARIANTS.h3}
                        />
                      </Box>
                      <Text
                        text={client.description}
                        variant={TEXT_VARIANTS.body1}
                      />
                    </div>
                  )}
                </div>
                {!isNew && (
                  <Box flexDirection='column' gridGap='10px' display='flex'>
                    <Button
                      className={classes.roundedButton}
                      onClick={setIsEditMode.toggle}
                      variant='outlined'
                      disabled={isRemovingClient}
                    >
                      {editMode ? 'Editing' : 'Edit'}
                    </Button>
                    <Button
                      className={classes.roundedButton}
                      onClick={() => setDeleteDialog.on()}
                      variant='outlined'
                      disabled={isRemovingClient}
                    >
                      Delete
                    </Button>
                  </Box>
                )}
              </div>

              <Divider additionalClasses={classes.divider} />

              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <ClientAccountsAssignment
                    editMode={editMode}
                    showAssignedAccountsTable={!isNew}
                    selectedAccounts={selectedAccounts}
                    onSearchAccounts={onSearchAccounts}
                    accountsAssigned={accountsAssigned}
                    accountsUnAssigned={accountsUnAssigned}
                    searchResultOptions={searchResultOptions}
                    onAssignUnAssignAccount={onAssignUnAssignAccount}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  {isBillingActive && (
                    <ClientBillingDetails editMode={editMode} />
                  )}
                  {!isNew && <ClientPersons clientId={params.id} />}
                </Grid>
              </Grid>

              <Divider additionalClasses={classes.divider} />

              <ClientDetailsTags editMode={editMode} />

              <Divider additionalClasses={classes.divider} />

              {editMode && (
                <SaveCancelButtons
                  containerClassName={classes.formActions}
                  onCancel={onCancel}
                  isSubmitting={formSubmitting}
                  isFormValid={isFormValid}
                />
              )}
            </form>
          </FormProvider>
        )}
      </div>
      <Snackbar
        open={openAlert}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity={alertSeverity}>
          {alertMessage}
        </Alert>
      </Snackbar>
      <Dialog open={deleteDialog} onClose={handleCloseDialog}>
        <Dialog.Header>
          Are you sure you want to delete {client?.longName}?
        </Dialog.Header>
        <Dialog.Actions className={classes.modalActions}>
          <RoundedButton
            onClick={handleCloseDialog}
            className={clsx(classes.button, classes.cancelBtn)}
          >
            Cancel
          </RoundedButton>
          <RoundedButton
            onClick={onRemoveClient}
            className={clsx(classes.button, classes.confirmBtn)}
          >
            Delete
          </RoundedButton>
        </Dialog.Actions>
      </Dialog>
    </>
  )
}

export default ClientDetailsView
